'use client';

import {
  Swap,
  SwapAmountInput,
  SwapButton,
  SwapMessage,
  SwapToast,
  SwapToggleButton,
} from '@coinbase/onchainkit/swap';
import type { Token } from '@coinbase/onchainkit/token';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import WalletWrapper from 'src/components/WalletWrapper';
import { useViewport } from 'src/hooks/useViewport';
import { useAccount } from 'wagmi';

export default function HeroSection() {
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const { isConnected } = useAccount();
  const { isMobile, isClient } = useViewport();

  // Determine which UI to show based on viewport
  const showMobileUI = isMobile;
  const showDesktopUI = isClient && !isMobile;

  const tokens = {
    ETHToken: {
      address: '' as `0x${string}`,
      chainId: 8453,
      decimals: 18,
      name: 'Ethereum',
      symbol: 'ETH',
      image:
        'https://dynamic-assets.coinbase.com/dbb4b4983bde81309ddab83eb598358eb44375b930b94687ebe38bc22e52c3b2125258ffb8477a5ef22e33d6bd72e32a506c391caa13af64c00e46613c3e5806/asset_icons/4113b082d21cc5fab17fc8f2d19fb996165bcce635e6900f7fc2d57c4ef33ae9.png',
    },
    BoomerToken: {
      address: '0xcdE172dc5ffC46D228838446c57C1227e0B82049' as `0x${string}`,
      chainId: 8453,
      decimals: 18,
      name: 'Boomer',
      symbol: 'BOOMER',
      image:
        'https://assets.coingecko.com/coins/images/36477/standard/Token_Image_200x200.png?1714757172',
    },
  };

  const [fromToken, setFromToken] = useState<Token>(tokens.ETHToken);
  const [toToken, setToToken] = useState<Token>(tokens.BoomerToken);

  const closeModal = () => {
    setIsSwapModalOpen(false);
    setFromToken(tokens.ETHToken);
    setToToken(tokens.BoomerToken);
  };

  return (
    <section className="relative flex flex-col items-center justify-center overflow-hidden">
      <div className="relative w-full">
        <Image
          src="https://imagedelivery.net/U-2Wo4cuiNOrx5V-JQ4QOA/a6936f8f-a2f5-4f10-5315-7ae3159a8b00/public"
          alt="Boomer Hero"
          width={1920}
          height={1080}
          priority={true}
          quality={90}
          className='h-auto w-full'
        />
        {/* Desktop buttons overlay - only render on client if desktop detected */}
        {showDesktopUI && (
          <div className='absolute inset-x-0 bottom-12 z-10'>
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="flex flex-col items-center gap-4"
              >
                {isConnected ? (
                  <button
                    onClick={() => setIsSwapModalOpen(true)}
                    className='group relative inline-flex items-center overflow-hidden rounded-lg bg-blue-600 px-8 py-4 font-bold text-white text-xl transition-all duration-300 hover:bg-blue-500'
                  >
                    <span className='absolute inset-0 h-full w-full translate-x-[-200%] bg-gradient-to-r from-blue-400/0 via-blue-400/20 to-blue-400/0 transition-transform duration-1000 group-hover:translate-x-[200%]' />
                    <span className="relative flex items-center">
                      BUY SOME BOOMER
                      <span className='material-icon ml-2 transform transition-transform group-hover:translate-x-1'>
                        arrow_forward
                      </span>
                    </span>
                    <span className='absolute inset-0 rounded-lg border-2 border-blue-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100' />
                  </button>
                ) : (
                  <div className='flex w-full max-w-sm flex-col items-center gap-4'>
                    <WalletWrapper
                      text="BUY BOOMER"
                      className='group relative inline-flex w-full items-center justify-center overflow-hidden rounded-lg bg-blue-600 px-8 py-4 font-bold text-white text-xl transition-all duration-300 hover:bg-blue-500'
                    />
                  </div>
                )}

                {/* Telegram button - always visible */}
                <Link
                  href="https://t.co/wkSI8Su1Da"
                  target="_blank"
                  className='group relative mt-4 inline-flex w-full max-w-sm items-center justify-center overflow-hidden rounded-lg bg-blue-600 px-8 py-4 font-bold text-white text-xl transition-all duration-300 hover:bg-blue-500'
                >
                  <span className='absolute inset-0 h-full w-full translate-x-[-200%] bg-gradient-to-r from-blue-400/0 via-blue-400/20 to-blue-400/0 transition-transform duration-1000 group-hover:translate-x-[200%]' />
                  <span className="relative flex items-center">
                    Join us
                    <svg
                      className='ml-2 h-6 w-6 transform transition-transform group-hover:translate-x-1'
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.11.02-1.93 1.23-5.46 3.62-.52.36-.99.53-1.41.52-.46-.01-1.35-.26-2.01-.48-.81-.27-1.45-.42-1.4-.89.03-.24.37-.49 1.02-.75 4.04-1.76 6.74-2.92 8.09-3.48 3.85-1.6 4.64-1.88 5.17-1.89.11 0 .37.03.54.17.14.12.18.28.2.45-.02.14-.02.3-.03.42z" />
                    </svg>
                  </span>
                  <span className='absolute inset-0 rounded-lg border-2 border-blue-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100' />
                </Link>
              </motion.div>
            </div>
          </div>
        )}
      </div>
      {/* Mobile buttons section - use the isClient check instead of media queries */}
      {isClient && showMobileUI && (
        <div className="w-full bg-gray-900 py-12">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center gap-4"
            >
              {isConnected ? (
                <button
                  onClick={() => setIsSwapModalOpen(true)}
                  className='group relative inline-flex items-center overflow-hidden rounded-lg bg-blue-600 px-8 py-4 font-bold text-white text-xl transition-all duration-300 hover:bg-blue-500'
                >
                  <span className='absolute inset-0 h-full w-full translate-x-[-200%] bg-gradient-to-r from-blue-400/0 via-blue-400/20 to-blue-400/0 transition-transform duration-1000 group-hover:translate-x-[200%]' />
                  <span className="relative flex items-center">
                    BUY SOME BOOMER
                    <span className='material-icon ml-2 transform transition-transform group-hover:translate-x-1'>
                      arrow_forward
                    </span>
                  </span>
                  <span className='absolute inset-0 rounded-lg border-2 border-blue-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100' />
                </button>
              ) : (
                <div className='flex w-full max-w-sm flex-col items-center gap-4'>
                  <WalletWrapper
                    text="BUY BOOMER"
                    className='group relative inline-flex w-full items-center justify-center overflow-hidden rounded-lg bg-blue-600 px-8 py-4 font-bold text-white text-xl transition-all duration-300 hover:bg-blue-500'
                  />
                </div>
              )}

              {/* Telegram button - always visible */}
              <Link
                href="https://t.co/wkSI8Su1Da"
                target="_blank"
                className='group relative mt-4 inline-flex w-full max-w-sm items-center justify-center overflow-hidden rounded-lg bg-blue-600 px-8 py-4 font-bold text-white text-xl transition-all duration-300 hover:bg-blue-500'
              >
                <span className='absolute inset-0 h-full w-full translate-x-[-200%] bg-gradient-to-r from-blue-400/0 via-blue-400/20 to-blue-400/0 transition-transform duration-1000 group-hover:translate-x-[200%]' />
                <span className="relative flex items-center">
                  Join us
                  <svg
                    className='ml-2 h-6 w-6 transform transition-transform group-hover:translate-x-1'
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.11.02-1.93 1.23-5.46 3.62-.52.36-.99.53-1.41.52-.46-.01-1.35-.26-2.01-.48-.81-.27-1.45-.42-1.4-.89.03-.24.37-.49 1.02-.75 4.04-1.76 6.74-2.92 8.09-3.48 3.85-1.6 4.64-1.88 5.17-1.89.11 0 .37.03.54.17.14.12.18.28.2.45-.02.14-.02.3-.03.42z" />
                  </svg>
                </span>
                <span className='absolute inset-0 rounded-lg border-2 border-blue-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100' />
              </Link>
            </motion.div>
          </div>
        </div>
      )}

      {/* Swap Modal */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isSwapModalOpen ? 1 : 0 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm ${isSwapModalOpen ? 'block' : 'hidden'}`}
        onClick={closeModal}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className='mx-4 w-full max-w-md rounded-xl bg-gray-900 p-6'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='mb-6 flex items-center justify-between'>
            <h2 className='font-bold text-2xl'>Swap Tokens</h2>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
          <Swap experimental={{ useAggregator: true }}>
            <SwapAmountInput
              label="Sell"
              token={fromToken}
              type="from"
              swappableTokens={Object.values(tokens)}
            />
            <SwapToggleButton className="my-2" />
            <SwapAmountInput
              label="Buy"
              token={toToken}
              type="to"
              swappableTokens={Object.values(tokens)}
            />
            <SwapButton className='mt-6 w-full rounded-lg bg-blue py-3 hover:bg-blue-light' />
            <SwapMessage />
            <SwapToast />
          </Swap>
        </motion.div>
      </motion.div>
    </section>
  );
}
